/* eslint-disable no-console */

// PWA update flow is documented in docs/pwa-update-mechanics.md — keep that doc in sync with changes here.
import { register } from "register-service-worker";

// Broadcast channel for multi-tab coordination
const updateChannel = new BroadcastChannel("sw-update-check");

// Track if update notification is currently shown
let updateNotificationShown = false;

function showUpdateNotification() {
  if (updateNotificationShown) return;
  updateNotificationShown = true;

  // Create a simple notification banner
  const banner = document.createElement("div");
  banner.id = "update-notification";
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #4CAF50;
    color: white;
    padding: 16px;
    text-align: center;
    z-index: 10000;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;
  banner.innerHTML = `
    <strong>Uusi sovellusversio saatavilla!</strong>
    <button id="update-btn" style="
      margin-left: 16px;
      padding: 8px 16px;
      background: white;
      color: #4CAF50;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: bold;
    ">Päivitä nyt</button>
    <button id="dismiss-btn" style="
      margin-left: 8px;
      padding: 8px 16px;
      background: transparent;
      color: white;
      border: 1px solid white;
      border-radius: 4px;
      cursor: pointer;
    ">Ohita</button>
  `;
  document.body.appendChild(banner);

  // Refresh button handler
  document.getElementById("update-btn")?.addEventListener("click", () => {
    void (async () => {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        // Reload only after the new SW confirms it has taken control, avoiding
        // the race condition where reload happens before skipWaiting completes
        // and the banner reappears on the next page load.
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          () => {
            updateChannel.postMessage({ type: MessageType.RELOAD });
            window.location.reload();
          },
          { once: true },
        );
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      } else {
        console.error(
          "Update accepted, but registration or waiting is not available!",
          registration,
        );
        updateChannel.postMessage({ type: MessageType.RELOAD });
        window.location.reload();
      }
    })();
  });

  // Dismiss button handler
  document.getElementById("dismiss-btn")?.addEventListener("click", () => {
    banner.remove();
    updateNotificationShown = false;
  });
}

enum MessageType {
  UPDATE_AVAILABLE = "UPDATE_AVAILABLE",
  RELOAD = "RELOAD",
  REQUEST_SKIP_WAITING = "REQUEST_SKIP_WAITING",
}

type MessageEventData = {
  type: MessageType;
};

function isValidSwMessageEvent(
  event: MessageEvent<unknown>,
): event is MessageEvent<MessageEventData> {
  console.log("isValidSwMessageEvent event=", event);

  try {
    if (typeof event.data !== "object") return false;

    const { data } = event as MessageEvent<MessageEventData>;

    if (data.type === undefined || typeof data.type !== "string") return false;

    if (
      !Object.values(MessageType)
        .map((t) => t.toString())
        .includes(data.type)
    )
      return false;

    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

if (process.env.NODE_ENV === "production") {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {
      console.log(
        "App is being served from cache by a service worker.\n" +
          "For more details, visit https://goo.gl/AFskqB",
      );
    },
    registered(reg) {
      console.log("Service worker has been registered.");
      // Poll for updates every 15 minutes
      // Only one tab will actually make the request thanks to HTTP cache
      setInterval(
        async () => {
          console.log("Checking for service worker updates...");
          await reg.update();
        },
        15 * 60 * 1000,
      );
    },
    cached() {
      console.log("Content has been cached for offline use.");
    },
    updatefound() {
      console.log("New content is downloading.");
    },
    updated() {
      console.log("New content is available!");
      // Notify all tabs that notification should be shown
      updateChannel.postMessage({
        type: MessageType.UPDATE_AVAILABLE,
      });
    },
    offline() {
      console.log(
        "No internet connection found. App is running in offline mode.",
      );
    },
    error(error) {
      console.error("Error during service worker registration:", error);
    },
  });

  // Listen for messages from other tabs
  updateChannel.addEventListener("message", (event) => {
    if (!isValidSwMessageEvent(event)) {
      console.error(
        `Unexpected event format for a service worker updateChannel event`,
        event,
      );
    } else if (event.data.type === MessageType.UPDATE_AVAILABLE) {
      // Another tab found an update, show notification here too
      showUpdateNotification();
    } else if (event.data.type === MessageType.RELOAD) {
      // Another tab initiated reload, reload this tab too
      window.location.reload();
    } else {
      console.warn("Unexpected event type", event);
    }
    // else if (event.data.type === MessageType.REQUEST_SKIP_WAITING) { // Only the one tab that discovered the new
    //     registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    // }
  });
}
